import { green } from "@ant-design/colors"
import { faAdd, faEdit, faLink, faSave, faTimes, faTrash, faUnlink } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, Form, Input, Popconfirm, Space, Table, TableColumnType, Typography } from 'antd'
import { useState } from 'react'
import { hasValue } from '../../../helpers/nullable'
import VSpace from '../../common/VSpace'
import ThemableButton from '../../Items/ThemableButton'
import ToggleablePopconfirm from '../Divisions/ToggleablePopconfirm'
import LinkCostCodeModal from "./LinkCostCodeModal"
import { useCostCodes, useDeleteCostCode, useUpdateCostCode } from "../../../domain/costCodes/costCodeQueries"
import { CostCode } from "../../../domain/costCodes/costCode"

const NEW_COST_CODE_ID = -1
const CostCodes = () => {
  const costCodesQuery = useCostCodes()
  const updateCostCodeMutation = useUpdateCostCode()
  const deleteCostCodeMutation = useDeleteCostCode()

  const [form] = Form.useForm()
  const [editingCostCodeId, setEditingCostCodeId] = useState<number | undefined>()
  const editingCostCode = costCodesQuery.data?.find(c => c.id === editingCostCodeId)
  const editingCostCodeQboId = Form.useWatch('qboId', form)
  const editingCostCodeQboDisplayName = Form.useWatch('qboDisplayName', form)

  const [addedCostCode, setAddedCostCode] = useState<CostCode[]>([])
  const costCodes = [...addedCostCode, ...(costCodesQuery.data ?? [])]

  const shouldConfirmAdd = () => hasValue(editingCostCodeId) && form.isFieldsTouched()
  const shouldConfirmEdit = (id: number) => hasValue(editingCostCodeId) && editingCostCodeId !== id && form.isFieldsTouched()
  const shouldConfirmCancel = () => form.isFieldsTouched()

  const [showLinkCostCodeModal, setShowLinkCostCodeModal] = useState(false)

  const handleAdd = () => {
    const newCostCode: CostCode = {
      id: NEW_COST_CODE_ID,
      name: '',
      revenue: true,
      qboDisplayName: null,
      qboId: null,
    }
    setAddedCostCode([newCostCode])
    form.setFieldsValue(newCostCode)
    setEditingCostCodeId(NEW_COST_CODE_ID)
  }

  const editCostCode = (id: number) => {
    if (editingCostCodeId === id) {
      cancelEdit()
    }
    setEditingCostCodeId(id)
    setTimeout(() => form.setFieldsValue(costCodes.find(x => x.id === id)), 1)
  }

  const cancelEdit = () => {
    if (editingCostCodeId === NEW_COST_CODE_ID) {
      setAddedCostCode([])
    }
    setEditingCostCodeId(undefined)
    form.resetFields()
  }

  const saveCostCode = async () => {
    try {
      await form.validateFields()
      updateCostCodeMutation.mutate(form.getFieldsValue())
      setEditingCostCodeId(undefined)
      setAddedCostCode([])
      form.resetFields()
    } catch { }
  }

  const handleLinkCostCode = (qboId?: string, qboDisplayName?: string) => {
    if (qboId && qboDisplayName) {
      form.setFieldsValue({ qboId, qboDisplayName })
    }
    setShowLinkCostCodeModal(false)
  }

  const handleDelete = (e: React.MouseEvent<HTMLElement>, id: number) => {
    e.stopPropagation()
    e.preventDefault()
    deleteCostCodeMutation.mutate(id)
  }

  const columns: TableColumnType<CostCode>[] = [
    {
      key: 'name',
      title: 'Name',
      dataIndex: 'name',
      render: (value, record) => {
        if (record.id === editingCostCodeId) {
          return <Form.Item name="name">
            <Input />
          </Form.Item>
        }

        return value
      }
    },
    {
      key: 'qboDisplayName',
      title: 'QuickBooks Online Entry',
      render: (record) => {
        if (record.id === editingCostCodeId) {
          return <>
            <Form.Item>
              <Space>
                {editingCostCodeQboId
                  ? <Button type="link" onClick={() => setShowLinkCostCodeModal(true)}>{editingCostCodeQboDisplayName ?? editingCostCodeQboId}</Button>
                  : <ThemableButton
                    type="primary"
                    ghost
                    icon={<FontAwesomeIcon icon={faLink} />}
                    colorPrimary={green.primary}
                    onClick={() => setShowLinkCostCodeModal(true)}
                  >
                    Link to QuickBooks
                  </ThemableButton>
                }
                {editingCostCodeQboId &&
                  <Button
                    type="link"
                    danger
                    onClick={() => form.setFieldsValue({ qboId: undefined, qboDisplayName: undefined })}
                    icon={<FontAwesomeIcon icon={faUnlink} />}
                  />
                }
              </Space>
            </Form.Item>
            <Form.Item hidden name="qboId">
              <Input type="hidden" />
            </Form.Item>
            <Form.Item hidden name="qboDisplayName">
              <Input type="hidden" />
            </Form.Item>
          </>
        }

        return record.qboDisplayName ?? record.qboId ?? <Typography.Text type="danger">Not linked</Typography.Text>
      }
    },
    {
      key: 'actions',
      align: 'center',
      render: (_, record) => {
        const buttons: JSX.Element[] = []
        if (record.id === editingCostCodeId) {
          buttons.push(
            <Button
              key="save"
              type="link"
              onClick={saveCostCode}
              icon={<FontAwesomeIcon icon={faSave} />}
            />)
          buttons.push(
            <ToggleablePopconfirm
              title="Are you sure you want to discard your changes?"
              shouldConfirm={shouldConfirmCancel}
              onConfirm={cancelEdit}
              key="cancel"
            >
              <Button
                type="link"
                danger
                icon={<FontAwesomeIcon icon={faTimes} />}
              />
            </ToggleablePopconfirm>
          )
        } else {
          buttons.push(
            <ToggleablePopconfirm
              title="Another cost code is being edited. Are you sure you want to edit this one?"
              shouldConfirm={() => shouldConfirmEdit(record.id)}
              onConfirm={() => editCostCode(record.id)}
              key="edit"
            >
              <Button
                type="link"
                icon={<FontAwesomeIcon icon={faEdit} />}
              />
            </ToggleablePopconfirm>

          )
        }

        buttons.push(<Popconfirm
          key="delete"
          title="Are you sure want to delete this cost code?"
          onConfirm={e => handleDelete(e!, record.id)}
        >
          <Button
            danger
            type="link"
            icon={<FontAwesomeIcon icon={faTrash} />}
          />
        </Popconfirm>)

        return <Form.Item>
          <Space>
            {buttons}
          </Space>
        </Form.Item>
      }
    }
  ]

  return <>
    <VSpace>
      <ToggleablePopconfirm
        title="Another cost code is already being edited. Do you want to discard those changes?"
        shouldConfirm={shouldConfirmAdd}
        onConfirm={handleAdd}
      >
        <Button
          icon={<FontAwesomeIcon icon={faAdd} />}
          type="primary"
        >
          Add Cost Code
        </Button>
      </ToggleablePopconfirm>
      <Form initialValues={editingCostCode} form={form}>
        <Form.Item hidden name="id">
          <Input type="hidden" />
        </Form.Item>
        <Table
          rowKey="id"
          loading={costCodesQuery.isLoading}
          columns={columns}
          dataSource={costCodes}
          pagination={false}
        />
      </Form>
    </VSpace>
    <LinkCostCodeModal
      open={showLinkCostCodeModal}
      onCancel={() => setShowLinkCostCodeModal(false)}
      onSubmit={handleLinkCostCode}
    />
  </>
}

export default CostCodes
